import type { DynamicConfig, FeatureGate, OverrideAdapter } from "@statsig/js-client";
import { z } from "zod";

const dynamicConfigOverridesSchema = z.record(z.string(), z.record(z.string(), z.unknown()));

/** The gate values in `gate1:true,gate2:false`. */
const parseGateOverrides = (raw: string | undefined): Map<string, boolean> => {
  const overrides = new Map<string, boolean>();
  for (const entry of raw?.split(",") ?? []) {
    const [gate, value] = entry.split(":");
    if (gate && value !== undefined) overrides.set(gate.trim(), value.trim() === "true");
  }
  return overrides;
};

/**
 * The config values in a JSON object keyed by config name. A value that does
 * not parse is dropped with a warning rather than thrown: a typo in a dev env
 * file must not stop the app from starting.
 */
const parseDynamicConfigOverrides = (
  raw: string | undefined,
): Map<string, Record<string, unknown>> => {
  if (!raw) return new Map();
  try {
    const parsed = dynamicConfigOverridesSchema.safeParse(JSON.parse(raw));
    if (parsed.success) return new Map(Object.entries(parsed.data));
  } catch {
    // Not JSON: warned below, the same as JSON of the wrong shape.
  }
  console.warn("VITE_STATSIG_DYNAMIC_CONFIG_OVERRIDES failed to parse; ignoring", raw);
  return new Map();
};

/**
 * An adapter that forces gate and dynamic config values locally, from the
 * `VITE_STATSIG_GATE_OVERRIDES` and `VITE_STATSIG_DYNAMIC_CONFIG_OVERRIDES` env
 * vars. They mirror the api's `STATSIG_GATE_OVERRIDES` and
 * `STATSIG_DYNAMIC_CONFIG_OVERRIDES` (`api/src/utils/statsig.ts`) and take the
 * same formats, so one value turns a feature on at both ends of a rig.
 *
 * A dynamic config override replaces the config's whole value, as the api's
 * does. Read it through `.value`: the config's `get()` keeps reading the value
 * Statsig evaluated.
 */
export const buildStatsigOverrideAdapter = ({
  dev,
  gates,
  dynamicConfigs,
}: {
  dev: boolean;
  gates?: string;
  dynamicConfigs?: string;
}): OverrideAdapter | undefined => {
  const gateOverrides = new Map<string, boolean>();

  // In local dev, default `internal-features` on so power-user UI (debug,
  // model switcher, admin link, …) is visible without extra setup. Mirrors the
  // old behavior where every dev user was an admin under NODE_ENV=dev.
  if (dev) gateOverrides.set("internal-features", true);
  // Explicit env overrides win over the dev default above.
  for (const [gate, value] of parseGateOverrides(gates)) gateOverrides.set(gate, value);

  const configOverrides = parseDynamicConfigOverrides(dynamicConfigs);

  if (gateOverrides.size === 0 && configOverrides.size === 0) return undefined;

  return {
    getGateOverride: (current: FeatureGate): FeatureGate | null => {
      const override = gateOverrides.get(current.name);
      if (override === undefined) return null;
      return { ...current, value: override };
    },
    getDynamicConfigOverride: (current: DynamicConfig): DynamicConfig | null => {
      const override = configOverrides.get(current.name);
      if (override === undefined) return null;
      return { ...current, value: override };
    },
  };
};
